import { Alert } from '@mui/joy';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetPartFinderFamilyParts } from 'companySettings/hooks';
import { IPartFinderFamily } from 'companySettings/types';
import { SlidingPanel } from 'core/components';
import { Loader } from 'core/elements';
import { FormProvider, NotificationProvider } from 'core/states';
import { Color, Variant } from 'theme/enums';

import { mapPartFinderFamilyFormDefaults } from './mappers';
import { upsertPartFinderFamilySchema } from './schema';
import { UpsertPartFinderFamilySteps } from './UpsertPartFinderFamilySteps';
import { mapPartFromServer } from './utils';

interface IProps {
  family?: IPartFinderFamily;
  isOpen: boolean;
  onClose: () => void;
}

// The Add Part Finder Family wizard. A family that is still a draft opens in it again, filled in,
// once its parts are loaded.
export const UpsertPartFinderFamily = ({ family, isOpen, onClose }: IProps) => {
  const { t } = useTranslation();

  const { data, isLoading, isError } = useGetPartFinderFamilyParts(
    family?.id,
    isOpen && Boolean(family)
  );

  const loadedParts = useMemo(() => (data?.records ?? []).map(mapPartFromServer), [data]);
  const defaultValues = useMemo(
    () => mapPartFinderFamilyFormDefaults(family, loadedParts),
    [family, loadedParts]
  );

  if (!isOpen) {
    return null;
  }

  // A saved family opens once its parts are loaded: a form without them would save it without them.
  if (family && (isLoading || isError)) {
    return (
      <SlidingPanel isOpen onClosePanel={onClose} title={t('titleAddPartFinderFamily')}>
        {isError ? (
          <Alert variant={Variant.SOFT} color={Color.DANGER}>
            {t('errorPartFinderFamilyPartsLoad')}
          </Alert>
        ) : (
          <Loader />
        )}
      </SlidingPanel>
    );
  }

  return (
    <NotificationProvider>
      <FormProvider schema={upsertPartFinderFamilySchema()} defaultValues={defaultValues}>
        <UpsertPartFinderFamilySteps family={family} loadedParts={loadedParts} onClose={onClose} />
      </FormProvider>
    </NotificationProvider>
  );
};
